/** Unified line diff for template sources (history dialog). jsdiff `diffLines`
 *  rendered as plain MUI boxes — no HTML injection anywhere. Long unchanged
 *  runs collapse to 3 lines of context around each change with an expandable
 *  "… N unchanged lines …" row.
 */
import { alpha, Box, ButtonBase, useTheme } from '@mui/material';
import { diffLines } from 'diff';
import { useMemo, useState } from 'react';

type RowType = 'add' | 'del' | 'ctx';

interface DiffRow {
  type: RowType;
  text: string;
}

type Segment =
  | { kind: 'rows'; rows: DiffRow[] }
  | { kind: 'collapsed'; id: number; rows: DiffRow[] };

const CONTEXT_LINES = 3;
const COLLAPSE_THRESHOLD = 7;

function splitPartLines(value: string): string[] {
  const lines = value.split('\n');
  // A part ending in '\n' yields a trailing empty string — not a real line.
  if (lines.length > 0 && lines[lines.length - 1] === '') lines.pop();
  return lines;
}

function buildSegments(before: string, after: string): Segment[] {
  const rows: DiffRow[] = [];
  for (const part of diffLines(before, after)) {
    const type: RowType = part.added ? 'add' : part.removed ? 'del' : 'ctx';
    for (const line of splitPartLines(part.value)) rows.push({ type, text: line });
  }

  const segments: Segment[] = [];
  let collapsedId = 0;
  let i = 0;
  while (i < rows.length) {
    const row = rows[i];
    if (!row) break;
    if (row.type !== 'ctx') {
      const changed: DiffRow[] = [];
      while (i < rows.length && rows[i]?.type !== 'ctx') {
        changed.push(rows[i] as DiffRow);
        i += 1;
      }
      segments.push({ kind: 'rows', rows: changed });
      continue;
    }
    const run: DiffRow[] = [];
    while (i < rows.length && rows[i]?.type === 'ctx') {
      run.push(rows[i] as DiffRow);
      i += 1;
    }
    if (run.length <= COLLAPSE_THRESHOLD) {
      segments.push({ kind: 'rows', rows: run });
    } else {
      segments.push({ kind: 'rows', rows: run.slice(0, CONTEXT_LINES) });
      segments.push({
        kind: 'collapsed',
        id: collapsedId,
        rows: run.slice(CONTEXT_LINES, run.length - CONTEXT_LINES),
      });
      collapsedId += 1;
      segments.push({ kind: 'rows', rows: run.slice(run.length - CONTEXT_LINES) });
    }
  }
  return segments;
}

interface Props {
  before: string;
  after: string;
  maxHeight?: number;
}

export function TextDiffView({ before, after, maxHeight = 320 }: Props) {
  const theme = useTheme();
  const [expanded, setExpanded] = useState<ReadonlySet<number>>(new Set());

  const segments = useMemo(() => buildSegments(before, after), [before, after]);

  const rowSx = {
    px: 1,
    whiteSpace: 'pre-wrap' as const,
    wordBreak: 'break-word' as const,
  };
  const bg: Record<RowType, string | undefined> = {
    add: alpha(theme.palette.success.light, 0.3),
    del: alpha(theme.palette.error.light, 0.3),
    ctx: undefined,
  };
  const prefix: Record<RowType, string> = { add: '+ ', del: '- ', ctx: '  ' };

  const renderRows = (rows: DiffRow[], keyPrefix: string) =>
    rows.map((row, idx) => (
      <Box key={`${keyPrefix}-${idx}`} sx={{ ...rowSx, bgcolor: bg[row.type] }}>
        {prefix[row.type]}
        {row.text}
      </Box>
    ));

  return (
    <Box
      sx={{
        fontFamily: 'monospace',
        fontSize: 12,
        lineHeight: 1.6,
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'auto',
        maxHeight,
      }}
    >
      {segments.map((seg, idx) => {
        if (seg.kind === 'rows') return renderRows(seg.rows, `s${idx}`);
        if (expanded.has(seg.id)) return renderRows(seg.rows, `s${idx}`);
        return (
          <ButtonBase
            key={`s${idx}`}
            onClick={() => setExpanded((prev) => new Set(prev).add(seg.id))}
            sx={{
              display: 'block',
              width: '100%',
              px: 1,
              py: 0.25,
              textAlign: 'center',
              fontFamily: 'monospace',
              fontSize: 12,
              color: 'text.secondary',
              bgcolor: 'action.hover',
            }}
          >
            … {seg.rows.length} unchanged line{seg.rows.length === 1 ? '' : 's'} …
          </ButtonBase>
        );
      })}
      {segments.length === 0 && (
        <Box sx={{ ...rowSx, color: 'text.secondary' }}>(both sides empty)</Box>
      )}
    </Box>
  );
}
